import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDoctorStore } from '@/stores/doctorStore'
import { useHospitalStore } from '@/stores/hospitalStore'
import { usePatientStore } from '@/stores/patientStore'

export default function DoctorList() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([])
  const [filterHospital, setFilterHospital] = useState<string>('all')

  const { doctors, deleteDoctor } = useDoctorStore()
  const { hospitals } = useHospitalStore()
  const { patients } = usePatientStore()

  // 医師ごとの担当患者数を計算
  const doctorsWithCounts = doctors.map(doctor => {
    const patientCount = patients.filter(p => p.primaryDoctorId === doctor.id || p.transplantDoctorId === doctor.id).length
    return {
      ...doctor,
      patientCount,
    }
  })

  const filteredDoctors = doctorsWithCounts.filter((doctor) => {
    const matchesSearch =
      doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.hospitalName.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesHospital =
      filterHospital === 'all' || doctor.hospitalId === filterHospital
    return matchesSearch && matchesHospital
  })

  const toggleSelectAll = () => {
    if (selectedDoctors.length === filteredDoctors.length) {
      setSelectedDoctors([])
    } else {
      setSelectedDoctors(filteredDoctors.map((d) => d.id))
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedDoctors((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    )
  }

  const handleDeleteSelected = () => {
    if (confirm(`${selectedDoctors.length}件の医師を削除しますか？`)) {
      selectedDoctors.forEach(id => deleteDoctor(id))
      setSelectedDoctors([])
    }
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <h1 className="aip-main__header-title">医師マスター</h1>
          <div className="aip-main__header-buttons">
            <Link to="/master/doctors/create" className="aip-button aip-button--solid-fill">
              <span className="material-symbols-outlined">add</span>
              新規登録
            </Link>
          </div>
        </div>
      </header>

      <div className="aip-main__content">
        <div className="list-container">
          {/* ツールバー */}
          <div className="aip-table__header">
            <div className="aip-table__header-area">
              <div className="aip-search-bar">
                <span className="material-symbols-outlined aip-search-bar__icon">search</span>
                <input
                  className="aip-search-bar__input"
                  type="text"
                  placeholder="医師名、ID、病院名で検索"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select
                className="aip-select"
                value={filterHospital}
                onChange={(e) => setFilterHospital(e.target.value)}
              >
                <option value="all">すべての病院</option>
                {hospitals.map((hospital) => (
                  <option key={hospital.id} value={hospital.id}>{hospital.name}</option>
                ))}
              </select>
            </div>
            <div className="aip-table__header-area">
              <span className="result-count">{filteredDoctors.length}件</span>
            </div>
          </div>

          {/* 選択時のアクションバー */}
          {selectedDoctors.length > 0 && (
            <div className="aip-table__selection-bar">
              <span>{selectedDoctors.length}件選択中</span>
              <button className="aip-button aip-button--outline" onClick={handleDeleteSelected}>
                <span className="material-symbols-outlined">delete</span>
                削除
              </button>
            </div>
          )}

          {/* テーブル */}
          <div className="aip-table-wrapper">
            <table className="aip-table">
              <thead>
                <tr>
                  <th className="aip-table__checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedDoctors.length === filteredDoctors.length && filteredDoctors.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>医師ID</th>
                  <th>氏名</th>
                  <th>所属病院</th>
                  <th>専門</th>
                  <th>メール</th>
                  <th>担当患者数</th>
                  <th>状態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredDoctors.map((doctor) => (
                  <tr key={doctor.id}>
                    <td className="aip-table__checkbox-cell">
                      <input
                        type="checkbox"
                        checked={selectedDoctors.includes(doctor.id)}
                        onChange={() => toggleSelect(doctor.id)}
                      />
                    </td>
                    <td>
                      <Link to={`/master/doctors/${doctor.id}`} className="aip-table__link">
                        {doctor.id}
                      </Link>
                    </td>
                    <td>
                      <div className="doctor-name-cell">
                        <div className="doctor-avatar">
                          {doctor.name.charAt(0)}
                        </div>
                        <div>
                          <span className="doctor-name">{doctor.name}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Link to={`/master/hospitals/${doctor.hospitalId}`} className="aip-table__link">
                        {doctor.hospitalName}
                      </Link>
                    </td>
                    <td>{doctor.specialty}</td>
                    <td>{doctor.email}</td>
                    <td className="text-center">{doctor.patientCount}</td>
                    <td>
                      <span className={`aip-badge aip-badge--${doctor.status === 'active' ? 'success' : 'neutral'}`}>
                        {doctor.status === 'active' ? '有効' : '無効'}
                      </span>
                    </td>
                    <td>
                      <div className="aip-table__actions">
                        <Link to={`/master/doctors/${doctor.id}`} className="aip-button aip-button--icon aip-button--sm" title="詳細">
                          <span className="material-symbols-outlined">visibility</span>
                        </Link>
                        <Link to={`/master/doctors/${doctor.id}/edit`} className="aip-button aip-button--icon aip-button--sm" title="編集">
                          <span className="material-symbols-outlined">edit</span>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ページネーション */}
          <div className="aip-table__pagination">
            <span className="aip-table__pagination-info">
              1 - {filteredDoctors.length} （{doctors.length}件中）
            </span>
            <div className="aip-table__pagination-controls">
              <button className="aip-button aip-button--icon" disabled>
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button className="aip-button aip-button--icon">
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .list-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .aip-table__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-md);
          border-bottom: 1px solid var(--border-color);
        }

        .aip-table__header-area {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          flex-wrap: wrap;
        }

        .aip-select {
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: var(--background-primary);
          max-width: 200px;
        }

        .result-count {
          font-size: 0.875rem;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .aip-table__selection-bar {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-blue50);
          border-bottom: 1px solid var(--color-blue200);
        }

        .aip-table-wrapper {
          overflow-x: auto;
        }

        .aip-table {
          width: 100%;
          border-collapse: collapse;
        }

        .aip-table th,
        .aip-table td {
          padding: var(--spacing-sm) var(--spacing-md);
          text-align: left;
          border-bottom: 1px solid var(--border-color);
          white-space: nowrap;
          font-size: 0.8125rem;
        }

        .aip-table th {
          background-color: var(--color-solid-gray25);
          font-weight: 500;
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .aip-table tbody tr:hover {
          background-color: var(--color-solid-gray25);
        }

        .aip-table__checkbox-cell {
          width: 40px;
          text-align: center !important;
        }

        .aip-table__link {
          color: var(--color-blue600);
          font-weight: 500;
        }

        .aip-table__link:hover {
          text-decoration: underline;
        }

        .doctor-name-cell {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
        }

        .doctor-avatar {
          width: 32px;
          height: 32px;
          border-radius: var(--border-radius-md);
          background-color: var(--color-blue500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .doctor-name {
          display: block;
          font-weight: 500;
        }

        .text-center {
          text-align: center;
        }

        .aip-badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 0.6875rem;
          font-weight: 500;
          border-radius: var(--border-radius-sm);
        }

        .aip-badge--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .aip-badge--neutral {
          background-color: var(--color-solid-gray100);
          color: var(--color-solid-gray700);
        }

        .aip-table__actions {
          display: flex;
          gap: var(--spacing-xs);
        }

        .aip-table__pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-md);
          border-top: 1px solid var(--border-color);
        }

        .aip-table__pagination-info {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .aip-table__pagination-controls {
          display: flex;
          gap: var(--spacing-xs);
        }

        .aip-button--sm {
          padding: 4px;
        }

        .aip-button--sm .material-symbols-outlined {
          font-size: 20px;
        }
      `}</style>
    </>
  )
}
